import type { BridgeHandlerName } from './types'

/**
 * 런타임 타입 검증 에러
 * Flutter가 예상과 다른 형태의 데이터를 반환했을 때 발생
 * 재시도해도 동일한 결과이므로 즉시 실패 처리해야 함
 */
export class BridgeValidationError extends Error {
  constructor(handler: string, message: string) {
    super(`[Bridge] "${handler}" validation failed: ${message}`)
    this.name = 'BridgeValidationError'
  }
}

/**
 * 핸들러별 응답 필드 검증 규칙
 * Flutter가 반환하는 값이 TypeScript 타입과 일치하는지 런타임에서 확인
 * as 캐스팅만으로는 잡을 수 없는 타입 불일치를 여기서 잡음
 *
 * 반환값: null이면 통과, 문자열이면 에러 메시지
 */
const RESPONSE_VALIDATORS: Partial<Record<BridgeHandlerName, (result: unknown) => string | null>> = {
  healthCheck: (result) => {
    if (typeof result !== 'object' || result === null || !('status' in result) || !('timestamp' in result)) {
      return 'expected { status: string, timestamp: string }'
    }
    return null
  },
  getToken: (result) => {
    if (typeof result !== 'object' || result === null || !('token' in result)) {
      return 'expected { token: string, bottom?: string }'
    }
    const token = (result as { token: unknown }).token
    if (typeof token !== 'string' || token.length === 0) {
      return 'token must be a non-empty string'
    }
    return null
  },
  getUserInfo: (result) => {
    // [핸들러 규약 v2 — 2026-05-15] 명세상 모든 필드 optional ("파라미터가 매번 바뀔 수 있어서 주의 필요").
    // Flutter 측이 어떤 조합으로도 응답 가능 (예: `{ isNewUserEvent: true }` 만, 또는 `{ userId: 'u1' }` 만).
    // 응답이 객체이기만 하면 통과. 각 필드는 있을 경우 타입만 검증.
    if (typeof result !== 'object' || result === null) {
      return 'expected object (all fields optional)'
    }
    const obj = result as { userId?: unknown; nickname?: unknown; isNewUserEvent?: unknown }
    if (obj.userId !== undefined && typeof obj.userId !== 'string') {
      return 'userId (if present) must be a string'
    }
    if (obj.nickname !== undefined && typeof obj.nickname !== 'string') {
      return 'nickname (if present) must be a string'
    }
    if (obj.isNewUserEvent !== undefined && typeof obj.isNewUserEvent !== 'boolean') {
      return 'isNewUserEvent (if present) must be a boolean'
    }
    return null
  },
  shareLink: (result) => {
    if (typeof result !== 'object' || result === null || !('shared' in result)) {
      return 'expected { shared: boolean }'
    }
    return null
  },
  openBottom: (result) => {
    if (typeof result !== 'object' || result === null || !('type' in result)) {
      return 'expected { type: "share" | "order" | "alarm" | "quiz" }'
    }
    const type = (result as { type: unknown }).type
    if (type !== 'share' && type !== 'order' && type !== 'alarm' && type !== 'quiz') {
      return 'type must be one of "share" | "order" | "alarm" | "quiz"'
    }
    return null
  },
  getServiceAvailability: (result) => {
    if (
      typeof result !== 'object' ||
      result === null ||
      !('address' in result) ||
      !('isAvailable' in result) ||
      !('waitingCount' in result)
    ) {
      return 'expected { address: string, isAvailable: boolean, waitingCount: number | null }'
    }
    const { address, isAvailable, waitingCount } = result as {
      address: unknown
      isAvailable: unknown
      waitingCount: unknown
    }
    if (typeof address !== 'string' || address.length === 0) {
      return 'address must be a non-empty string'
    }
    if (typeof isAvailable !== 'boolean') {
      return 'isAvailable must be a boolean'
    }
    if (waitingCount !== null && typeof waitingCount !== 'number') {
      return 'waitingCount must be a number or null'
    }
    return null
  },
  // ─── [핸들러 규약 v2 — 2026-05-15] 신규 validator ───────────────────
  getAuthToken: (result) => {
    if (
      typeof result !== 'object' ||
      result === null ||
      !('accessToken' in result) ||
      !('refreshToken' in result)
    ) {
      return 'expected { accessToken: string | null, refreshToken: string | null }'
    }
    const { accessToken, refreshToken } = result as { accessToken: unknown; refreshToken: unknown }
    if (accessToken !== null && typeof accessToken !== 'string') {
      return 'accessToken must be a string or null'
    }
    if (refreshToken !== null && typeof refreshToken !== 'string') {
      return 'refreshToken must be a string or null'
    }
    return null
  },
  getDeviceInfo: (result) => {
    if (typeof result !== 'object' || result === null) {
      return 'expected { topPadding, bottomPadding, deviceHeight, deviceWidth — each number | null }'
    }
    const obj = result as Record<string, unknown>
    for (const key of ['topPadding', 'bottomPadding', 'deviceHeight', 'deviceWidth'] as const) {
      const value = obj[key]
      if (value !== null && typeof value !== 'number') {
        return `${key} must be a number or null`
      }
    }
    return null
  },
  onSyncError: (result) => {
    if (typeof result !== 'object' || result === null || !('isSuccess' in result)) {
      return 'expected { isSuccess: true }'
    }
    if ((result as { isSuccess: unknown }).isSuccess !== true) {
      return 'isSuccess must be true'
    }
    return null
  },
  onNext: (result) => {
    if (typeof result !== 'object' || result === null || !('isSuccess' in result)) {
      return 'expected { isSuccess: boolean, message?: string }'
    }
    const { isSuccess, message } = result as { isSuccess: unknown; message?: unknown }
    if (typeof isSuccess !== 'boolean') {
      return 'isSuccess must be a boolean'
    }
    if (message !== undefined && typeof message !== 'string') {
      return 'message (if present) must be a string'
    }
    return null
  },
  onPrevious: (result) => {
    if (typeof result !== 'object' || result === null || !('isSuccess' in result)) {
      return 'expected { isSuccess: boolean, pages?: number, message?: string }'
    }
    const { isSuccess, pages, message } = result as {
      isSuccess: unknown
      pages?: unknown
      message?: unknown
    }
    if (typeof isSuccess !== 'boolean') {
      return 'isSuccess must be a boolean'
    }
    if (pages !== undefined && typeof pages !== 'number') {
      return 'pages (if present) must be a number'
    }
    if (message !== undefined && typeof message !== 'string') {
      return 'message (if present) must be a string'
    }
    return null
  },
  // ─────────────────────────────────────────────────────────────────────
}

/**
 * Flutter 응답을 핸들러 타입에 맞게 검증
 * void 응답(undefined, null)은 호출 전에 스킵해야 함
 */
export function validateResponse(name: BridgeHandlerName, result: unknown) {
  const validator = RESPONSE_VALIDATORS[name]
  if (!validator) return

  const error = validator(result)
  if (error) {
    throw new BridgeValidationError(name, `${error}, got ${JSON.stringify(result)}`)
  }
}
